/**
 * FastrCommandPalette — Inline command palette following fastr-lib's
 * FastrItem/FastrProvider interface pattern.
 *
 * Composes:
 * - fastr-lib: FastrItem, FastrProvider interfaces (matched here for browser compat)
 * - expressr-lib: Synthesis engine for AI actions
 * - convertr-lib: Format conversion (markdown→docx, etc.)
 *
 * Opens on Cmd/Ctrl+K in the editor. Actions are context-aware:
 * - With selection: Rewrite, Expand, Summarize, Change Tone, Simplify
 * - Without selection: Generate, Continue Writing
 *
 * When full fastr-ui is available, this can be replaced with:
 * ```tsx
 * <FastrMenu isOpen={...} items={items} onSelect={execute} />
 * ```
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ── Fastr-compatible types (matches fastr-lib interfaces) ──

interface FastrItem {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  keywords?: string[];
  action: () => Promise<void>;
}

interface FastrCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  items: FastrItem[];
  position?: { x: number; y: number };
  placeholder?: string;
  /** When provided, enables custom prompt mode — typed text becomes an AI instruction */
  onCustomPrompt?: (prompt: string) => Promise<void>;
}

// ── Component ──

export function FastrCommandPalette({ isOpen, onClose, items, position, placeholder, onCustomPrompt }: FastrCommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [executing, setExecuting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter items by query (simple fuzzy match)
  const filtered = query
    ? items.filter(item => {
        const q = query.toLowerCase();
        return (
          item.title.toLowerCase().includes(q) ||
          item.subtitle?.toLowerCase().includes(q) ||
          item.keywords?.some(k => k.toLowerCase().includes(q))
        );
      })
    : items;

  // Show custom prompt option when text doesn't match presets and callback exists
  const showCustomPrompt = onCustomPrompt && query.length > 2 && filtered.length < items.length;

  // Total items including custom prompt
  const totalItems = filtered.length + (showCustomPrompt ? 1 : 0);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setExecuting(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  const executeCustomPrompt = useCallback(async () => {
    if (!onCustomPrompt || !query.trim()) return;
    setExecuting(true);
    try {
      await onCustomPrompt(query.trim());
    } finally {
      setExecuting(false);
      onClose();
    }
  }, [onCustomPrompt, query, onClose]);

  // Keyboard nav
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (executing) return;
      // If custom prompt item is selected (last item when visible)
      if (showCustomPrompt && selectedIndex === filtered.length) {
        executeCustomPrompt();
      } else if (filtered[selectedIndex]) {
        filtered[selectedIndex].action();
        onClose();
      } else if (showCustomPrompt) {
        // No preset matched, enter sends as custom prompt
        executeCustomPrompt();
      }
    }
  }, [filtered, selectedIndex, onClose, totalItems, showCustomPrompt, executeCustomPrompt, executing]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 999,
        }}
      />
      {/* Palette */}
      <div style={{
        position: 'fixed',
        top: position?.y ?? '20%',
        left: position?.x ?? '50%',
        transform: position ? 'none' : 'translateX(-50%)',
        width: 420,
        maxHeight: 380,
        background: '#1a1a2e',
        border: '1px solid #3a3a5a',
        borderRadius: 8,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Search input */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #2a2a4a' }}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? 'Type an action...'}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: '#e0e0f0',
              fontSize: 14,
              outline: 'none',
              padding: '4px 0',
            }}
          />
        </div>
        {/* Results */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 && !showCustomPrompt && (
            <div style={{ padding: '16px', textAlign: 'center', opacity: 0.5, fontSize: 13 }}>
              No matching actions
            </div>
          )}
          {filtered.map((item, idx) => (
            <div
              key={item.id}
              onClick={() => { item.action(); onClose(); }}
              onMouseEnter={() => setSelectedIndex(idx)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: idx === selectedIndex ? '#2a2a4a' : 'transparent',
                borderLeft: idx === selectedIndex ? '2px solid #6c6cf0' : '2px solid transparent',
              }}
            >
              {item.icon && <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{item.icon}</span>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{item.title}</div>
                {item.subtitle && (
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 1 }}>{item.subtitle}</div>
                )}
              </div>
            </div>
          ))}
          {/* Custom prompt option — visible when typed text doesn't fully match presets */}
          {showCustomPrompt && (
            <div
              onClick={executeCustomPrompt}
              onMouseEnter={() => setSelectedIndex(filtered.length)}
              style={{
                padding: '8px 12px',
                cursor: executing ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: selectedIndex === filtered.length ? '#2a2a4a' : 'transparent',
                borderLeft: selectedIndex === filtered.length ? '2px solid #8b8bf0' : '2px solid transparent',
                borderTop: filtered.length > 0 ? '1px solid #2a2a4a' : 'none',
              }}
            >
              <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{'>'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {executing ? 'Applying...' : `Apply: "${query}"`}
                </div>
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 1 }}>
                  Custom AI instruction on selected text
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Footer hint */}
        <div style={{
          padding: '6px 12px',
          borderTop: '1px solid #2a2a4a',
          fontSize: 10,
          opacity: 0.4,
          display: 'flex',
          gap: 12,
        }}>
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          {onCustomPrompt && <span>type a custom instruction</span>}
          <span>esc close</span>
        </div>
      </div>
    </>
  );
}

// ── Action builders — creates FastrItems for expressr context ──

export interface SelectionContext {
  selectedText: string;
  fullContent: string;
  selectionStart: number;
  selectionEnd: number;
  documentTitle: string;
  format: string;
}

/**
 * Build the list of fastr actions available for the current context.
 * These follow the fastr-lib FastrProvider.search() pattern.
 */
export function buildExpressrActions(
  ctx: SelectionContext,
  callbacks: {
    onSynthesize: (prompt: string, selection: string) => Promise<string>;
    onReplace: (text: string) => void;
    onInsert: (text: string) => void;
    onOpenSynthesis: () => void;
  },
): FastrItem[] {
  const hasSelection = ctx.selectedText.length > 0;
  const items: FastrItem[] = [];

  if (hasSelection) {
    const sel = ctx.selectedText;
    const truncated = sel.length > 60 ? sel.slice(0, 60) + '...' : sel;

    items.push({
      id: 'rewrite',
      title: 'Rewrite',
      subtitle: `Rewrite: "${truncated}"`,
      icon: '✏',
      keywords: ['rewrite', 'rephrase', 'improve'],
      action: async () => {
        const result = await callbacks.onSynthesize(
          `Rewrite the following text, improving clarity and flow while preserving the meaning:\n\n${sel}`,
          sel
        );
        if (result) callbacks.onReplace(result);
      },
    });

    items.push({
      id: 'expand',
      title: 'Expand',
      subtitle: `Add detail and depth to selection`,
      icon: '↗',
      keywords: ['expand', 'elaborate', 'detail', 'more'],
      action: async () => {
        const result = await callbacks.onSynthesize(
          `Expand the following text with more detail, examples, and depth. Keep the same tone and style:\n\n${sel}`,
          sel
        );
        if (result) callbacks.onReplace(result);
      },
    });

    items.push({
      id: 'summarize',
      title: 'Summarize',
      subtitle: `Condense to key points`,
      icon: '◇',
      keywords: ['summarize', 'condense', 'shorten', 'brief'],
      action: async () => {
        const result = await callbacks.onSynthesize(
          `Summarize the following text concisely, preserving the key points:\n\n${sel}`,
          sel
        );
        if (result) callbacks.onReplace(result);
      },
    });

    items.push({
      id: 'simplify',
      title: 'Simplify',
      subtitle: `Make more accessible`,
      icon: '○',
      keywords: ['simplify', 'simple', 'accessible', 'plain'],
      action: async () => {
        const result = await callbacks.onSynthesize(
          `Simplify the following text. Make it more accessible without losing substance:\n\n${sel}`,
          sel
        );
        if (result) callbacks.onReplace(result);
      },
    });

    items.push({
      id: 'tone-professional',
      title: 'Professional Tone',
      subtitle: `Adjust to authoritative, business tone`,
      icon: '◆',
      keywords: ['tone', 'professional', 'business', 'formal'],
      action: async () => {
        const result = await callbacks.onSynthesize(
          `Rewrite the following text in a professional, authoritative tone suitable for business context:\n\n${sel}`,
          sel
        );
        if (result) callbacks.onReplace(result);
      },
    });

    items.push({
      id: 'tone-persuasive',
      title: 'Persuasive Tone',
      subtitle: `Make compelling and persuasive`,
      icon: '◆',
      keywords: ['tone', 'persuasive', 'compelling', 'convince'],
      action: async () => {
        const result = await callbacks.onSynthesize(
          `Rewrite the following text to be more persuasive. Build a compelling case, use emotional resonance:\n\n${sel}`,
          sel
        );
        if (result) callbacks.onReplace(result);
      },
    });

    items.push({
      id: 'tone-philosophical',
      title: 'Philosophical Tone',
      subtitle: `Deepen with philosophical resonance`,
      icon: '◆',
      keywords: ['tone', 'philosophical', 'deep', 'thoughtful'],
      action: async () => {
        const result = await callbacks.onSynthesize(
          `Rewrite the following text with philosophical depth. Ideas should build upon each other with inevitability. ` +
          `Guide the reader across a landscape, not through bullet points:\n\n${sel}`,
          sel
        );
        if (result) callbacks.onReplace(result);
      },
    });
  }

  // Always available
  items.push({
    id: 'continue',
    title: 'Continue Writing',
    subtitle: `Generate the next paragraph`,
    icon: '→',
    keywords: ['continue', 'next', 'write', 'generate'],
    action: async () => {
      const lastChunk = ctx.fullContent.slice(-1000);
      const result = await callbacks.onSynthesize(
        `Continue writing the following ${ctx.format} document titled "${ctx.documentTitle}". ` +
        `Write the next 1-2 paragraphs, maintaining the existing tone and style. ` +
        `Here is the end of the document so far:\n\n${lastChunk}`,
        ''
      );
      if (result) callbacks.onInsert(result);
    },
  });

  items.push({
    id: 'synthesis-panel',
    title: 'Open AI Panel',
    subtitle: `Full synthesis controls`,
    icon: '⚡',
    keywords: ['ai', 'synthesis', 'panel', 'generate'],
    action: async () => {
      callbacks.onOpenSynthesis();
    },
  });

  return items;
}
